import { gql } from "src/__generated__";
import { useQuery, useMutation } from "@apollo/client";
import {
	Button,
	Checkbox,
	CheckboxGroup,
	useCheckboxGroup,
} from "@chakra-ui/react";
import { useCategories } from "src/hooks/useCategories";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	EmploymentType,
	JobAdCommunicationMethod,
} from "src/__generated__/graphql";
import { employmentTypeToText } from "src/features/my-job-applications/utils";
import { useEffect } from "react";
import { Loading } from "src/components/Loading";
import { RequestFailed } from "src/components/RequestFailed";
import { useToast } from "@chakra-ui/react";
import { logEvent } from "src/utlis/analytics";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@chakra-ui/icons";

const GET_JOB_AD_NOTIFICATION_PREFERENCES =
	gql(`query GetJobAdNotificationPreferences {
  getJobAdNotificationPreferences {
    categories { id name }
    communicationMethods
    employmentTypes
  }
}`);

const CREATE_JOB_AD_NOTIFICATION_PREFERENCES =
	gql(`mutation CreateJobAdNotificationPreferences($categoryIds: [String!]!, $employmentTypes: [EmploymentType!]!, $communicationMethods: [JobAdCommunicationMethod!]!) {
  createJobAdNotificationPreferences(categoryIds: $categoryIds, employmentTypes: $employmentTypes, communicationMethods: $communicationMethods) {
    categories { id name }
    communicationMethods
    employmentTypes
  }
}`);

const UPDATE_JOB_AD_NOTIFICATION_PREFERENCES =
	gql(`mutation UpdateJobAdNotificationPreferences($categoryIds: [String!], $employmentTypes: [EmploymentType!], $communicationMethods: [JobAdCommunicationMethod!]) {
  updateJobAdNotificationPreferences(categoryIds: $categoryIds, employmentTypes: $employmentTypes, communicationMethods: $communicationMethods) {
    employmentTypes
    communicationMethods
    categories { id name }
  }
}`);

const communicationMethodMap = {
	EMAIL: "Email",
	SMS: "SMS",
	WHATSAPP: "WhatsApp",
};

const schema = z.object({
	categoryIds: z.array(z.string()),
	employmentTypes: z.array(z.nativeEnum(EmploymentType)),
	communicationMethods: z.array(z.nativeEnum(JobAdCommunicationMethod)),
});

type FormValues = z.infer<typeof schema>;

export default function NotificationPreferencesPage() {
	const toast = useToast();
	const navigate = useNavigate();

	const { data, loading, refetch } = useQuery(
		GET_JOB_AD_NOTIFICATION_PREFERENCES
	);
	const [createPreferences, createPreferencesResult] = useMutation(
		CREATE_JOB_AD_NOTIFICATION_PREFERENCES,
		{
			onCompleted: () => {
				logEvent("job_ad_notification_preferences_created", {
					categories: watch("categoryIds"),
					employmentTypes: watch("employmentTypes"),
					communicationMethods: watch("communicationMethods"),
				});

				toast({
					title: "Notification preferences submitted",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
			},
		}
	);
	const [updatePreferences, updatePreferencesResult] = useMutation(
		UPDATE_JOB_AD_NOTIFICATION_PREFERENCES,
		{
			onCompleted: () => {
				logEvent("job_ad_notification_preferences_updated", {
					categories: watch("categoryIds"),
					employmentTypes: watch("employmentTypes"),
					communicationMethods: watch("communicationMethods"),
				});

				toast({
					title: "Notification preferences updated",
					status: "success",
					duration: 3000,
					isClosable: true,
				});
			},
		}
	);

	const {
		categories,
		loading: categoriesLoading,
		error: categoriesError,
	} = useCategories();

	const { handleSubmit, setValue, watch } = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			categoryIds: [],
			employmentTypes: [],
			communicationMethods: [],
		},
	});

	useEffect(() => {
		if (data?.getJobAdNotificationPreferences) {
			setValue(
				"categoryIds",
				data.getJobAdNotificationPreferences.categories.map((c) => c.id)
			);
			setValue(
				"employmentTypes",
				data.getJobAdNotificationPreferences.employmentTypes
			);
			setValue(
				"communicationMethods",
				data.getJobAdNotificationPreferences.communicationMethods
			);
		}
	}, [data?.getJobAdNotificationPreferences, setValue]);

	const { getCheckboxProps: getRoleCheckboxProps, value: rolesCheckboxValues } =
		useCheckboxGroup({
			value: watch("categoryIds"),
			onChange: (values) => setValue("categoryIds", values as string[]),
		});

	const {
		getCheckboxProps: getEmploymentCheckboxProps,
		value: employmentCheckboxValues,
	} = useCheckboxGroup({
		value: watch("employmentTypes"),
		onChange: (values) =>
			setValue("employmentTypes", values as EmploymentType[]),
	});

	const {
		getCheckboxProps: getCommunicationCheckboxProps,
		value: communicationCheckboxValues,
	} = useCheckboxGroup({
		value: watch("communicationMethods"),
		onChange: (values) =>
			setValue("communicationMethods", values as JobAdCommunicationMethod[]),
	});

	const notificationPreferencesExist = !!data?.getJobAdNotificationPreferences;

	const onSubmit = async (formData: FormValues) => {
		if (notificationPreferencesExist) {
			await updatePreferences({ variables: formData });
		} else {
			await createPreferences({ variables: formData });
		}
		refetch();
	};

	if (!categories && !categoriesLoading) {
		return <RequestFailed text={categoriesError?.message} />;
	}

	if (createPreferencesResult.error) {
		return (
			<RequestFailed
				text={createPreferencesResult.error?.message}
				onRetry={createPreferencesResult.reset}
			/>
		);
	}

	if (updatePreferencesResult.error) {
		return (
			<RequestFailed
				text={updatePreferencesResult.error?.message}
				onRetry={updatePreferencesResult.reset}
			/>
		);
	}

	return (
		<div>
			{loading || categoriesLoading ? (
				<Loading />
			) : (
				<>
					<Button
						mb={{
							base: 4,
							lg: 6,
						}}
						colorScheme="prussianBlue"
						variant={"outline"}
						border={"1px solid #D7D7D7"}
						rounded={0}
						p={2}
						fontWeight={500}
						onClick={() => {
							navigate(-1);
						}}
						leftIcon={<ChevronLeftIcon w={5} h={5} />}
					>
						Back
					</Button>
					<h1 className="text-[#0E212D] text-[18px] lg:text-[23px] font-semibold mb-6 lg:mb-12">
						Job Notification Preferences
					</h1>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="space-y-8 lg:space-y-12 w-full lg:w-[80%]">
							<div className="grid grid-cols-1 lg:grid-cols-[2fr,3fr] gap-2 lg:gap-4 border-t-[1px] pt-6 lg:pt-10">
								<h2 className="text-[#0E212D] text-[18px] lg:text-[20px] font-medium">
									Role preferences
								</h2>
								<CheckboxGroup value={rolesCheckboxValues}>
									<div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
										{categories?.map((category) => (
											<Checkbox
												key={category.id}
												colorScheme="brand"
												{...getRoleCheckboxProps({ value: category.id })}
											>
												{category.name}
											</Checkbox>
										))}
									</div>
								</CheckboxGroup>
							</div>
							<div className="grid grid-cols-1 lg:grid-cols-[2fr,3fr] gap-2 lg:gap-4 border-t-[1px] pt-6 lg:pt-10">
								<h2 className="text-[#0E212D] text-[18px] lg:text-[20px] font-medium">
									Job type preferences
								</h2>
								<CheckboxGroup value={employmentCheckboxValues}>
									<div className="w-full grid grid-cols-[1fr,2fr] gap-2">
										{Object.values(EmploymentType).map((type) => (
											<Checkbox
												key={type}
												colorScheme="brand"
												{...getEmploymentCheckboxProps({ value: type })}
											>
												{employmentTypeToText(type)}
											</Checkbox>
										))}
									</div>
								</CheckboxGroup>
							</div>
							<div className="grid grid-cols-1 lg:grid-cols-[2fr,3fr] gap-2 lg:gap-4 border-y-[1px] py-6 lg:py-10">
								<h2 className="text-[#0E212D] text-[18px] lg:text-[20px] font-medium">
									Preferred <br className="hidden lg:block" /> communication
									channels
								</h2>
								<CheckboxGroup value={communicationCheckboxValues}>
									<div className="w-full grid grid-cols-3 gap-2">
										{Object.values(JobAdCommunicationMethod).map((method) => (
											<Checkbox
												key={method}
												colorScheme="brand"
												{...getCommunicationCheckboxProps({ value: method })}
											>
												{communicationMethodMap[method]}
											</Checkbox>
										))}
									</div>
								</CheckboxGroup>
							</div>
						</div>
						<div className="mt-8 lg:mt-10 flex justify-end w-full lg:w-[80%]">
							<Button
								colorScheme="brand"
								type="submit"
								isLoading={
									createPreferencesResult.loading ||
									updatePreferencesResult.loading
								}
							>
								{notificationPreferencesExist
									? "Update preferences"
									: "Submit preferences"}
							</Button>
						</div>
					</form>
				</>
			)}
		</div>
	);
}
